import type {NodeKey} from 'lexical';
import {
    DOMConversionMap,
    EditorConfig,
    LexicalNode,
    SerializedTextNode,
    Spread,
    TextNode
} from 'lexical';

type FontStyle = {
    size?: string;
    color?: string;
    family?: string;
};

export const nodeType = 'styled-text';

type SerializedFontNode = Spread<
    {
        fontStyles: FontStyle;
    },
    SerializedTextNode
>;

export class StyledTextNode extends TextNode {
    __nodeStyles: FontStyle;
    __fontStyles: FontStyle;

    constructor(text: string, style: FontStyle = {}, key?: NodeKey) {
        super(text, key);
        this.__nodeStyles = style;
    }

    isInline(): true {
        return true;
    }

    static getType(): string {
        return nodeType;
    }

    static clone(node: StyledTextNode): StyledTextNode {
        return new StyledTextNode(node.__text, node.__fontStyles, node.__key);
    }

    static importJSON(serializedNode: SerializedFontNode): StyledTextNode {
        return $createStyledTextNode(serializedNode.text, serializedNode.fontStyles);
    }

    static importDOM(): DOMConversionMap | null {
        return {
            span: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            },
            strong: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            },
            em: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            },
            i: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            },
            u: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            },
            b: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            },
            font: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            },
            s: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            },
            strike: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            }
        };
    }

    exportJSON(): SerializedFontNode {
        return {
            detail: this.getDetail(),
            format: this.getFormat(),
            mode: this.getMode(),
            style: this.getStyle(),
            text: this.getTextContent(),
            fontStyles: this.getFontStyles(),
            type: nodeType,
            version: 1
        };
    }

    getFontStyles(): FontStyle {
        const self = this.getLatest();
        return self.__nodeStyles;
    }

    createDOM(config: EditorConfig): HTMLElement {
        const element = super.createDOM(config);
        const {color, family, size} = this.__nodeStyles;
        if (color) element.style.color = color;
        if (family) element.style.fontFamily = family;
        if (size) element.style.fontSize = size;

        return element;
    }

    updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
        const isUpdated = super.updateDOM(prevNode, dom, config);
        if (prevNode.__nodeStyles !== this.__nodeStyles) {
            dom.style.color = this.__nodeStyles.color || '';
            dom.style.fontFamily = this.__nodeStyles.family || '';
            dom.style.fontSize = this.__nodeStyles.size || '';
        }
        return isUpdated;
    }
}

export const $styleNodeConversion = (node, element) => {
    // Function to recursively collect styles from nested elements
    // Process child elements first, then parent, so parent styles don't override child styles
    const collectStyles = (el, styles: Record<string, string> = {}) => {
        // First, recursively collect from child elements
        const children = el.children || [];
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child.nodeType === 1) {
                // Element node
                collectStyles(child, styles);
            }
        }

        // Then collect styles from current element (so child styles take precedence)
        if (el.style) {
            if (el.style.fontSize && !styles['font-size']) styles['font-size'] = el.style.fontSize;
            if (el.style.fontFamily && !styles['font-family'])
                styles['font-family'] = el.style.fontFamily;
            if (el.style.color && !styles.color) styles.color = el.style.color;
        }

        // Handle legacy <font> element attributes
        if (el.tagName === 'FONT') {
            if (el.getAttribute && el.getAttribute('face') && !styles['font-family']) {
                styles['font-family'] = el.getAttribute('face');
            }
            if (el.getAttribute && el.getAttribute('color') && !styles.color) {
                styles.color = el.getAttribute('color');
            }
            if (el.getAttribute && el.getAttribute('size') && !styles['font-size']) {
                // Convert HTML font size to CSS font size
                const fontSize = el.getAttribute('size');
                const sizeMap = {
                    '1': '10px',
                    '2': '13px',
                    '3': '16px',
                    '4': '18px',
                    '5': '24px',
                    '6': '32px',
                    '7': '48px'
                };
                styles['font-size'] = sizeMap[fontSize] || '16px';
            }
        }

        return styles;
    };

    // Collect all styles from the element tree
    const decorationStyles = collectStyles(element);

    // Create a text node with the collected styles
    const textNode = $createStyledTextNode(element.textContent || '', decorationStyles);

    // Check for nested formatting tags and apply their formatting
    let format = 0;

    // Check for italic formatting (nested <i> or <em> tags, or font-style)
    if (
        element.style?.fontStyle === 'italic' ||
        element.querySelector('i, em') ||
        element.tagName === 'I' ||
        element.tagName === 'EM'
    ) {
        format |= 2; // ITALIC format flag
    }

    // Check for underline formatting (nested <u> tags, or text-decoration)
    if (
        (element.style?.textDecoration && element.style.textDecoration.includes('underline')) ||
        element.querySelector('u') ||
        element.tagName === 'U'
    ) {
        format |= 8; // UNDERLINE format flag
    }

    // Check for bold formatting (nested <b>, <strong> tags, or font-weight)
    if (
        element.style?.fontWeight === 'bold' ||
        parseInt(element.style?.fontWeight) >= 600 ||
        element.querySelector('b, strong') ||
        element.tagName === 'B' ||
        element.tagName === 'STRONG'
    ) {
        format |= 1; // BOLD format flag
    }

    // Check for strikethrough formatting (nested <s>, <strike> tags, or text-decoration)
    if (
        (element.style?.textDecoration && element.style.textDecoration.includes('line-through')) ||
        element.querySelector('s, strike') ||
        element.tagName === 'S' ||
        element.tagName === 'STRIKE'
    ) {
        format |= 4; // STRIKETHROUGH format flag
    }

    // Apply the format to the text node
    if (format > 0) {
        textNode.setFormat(format);
    }

    return {
        node: textNode
    };
};

export function $createStyledTextNode(text: string, styles: FontStyle): any {
    const node = new TextNode(text);

    const stringStyles = Object.entries(styles)
        .filter(([key, value]: [string, string]) => value)
        .map(([key, value]: [string, string]) => `${key}: ${value};`)
        .join(' ');
    node.setStyle(stringStyles);
    return node;
}

export function $isStyledTextNode(node?: LexicalNode): boolean {
    return node instanceof StyledTextNode;
}
